<template>
	<aside
		class="property"
		:class="{ 'shrink': !isVisible, 'bg-color-editing': isUserEditing !== user.id && isUserEditing }">
		<div class="d-flex align-items-start justify-content-between">
			<div v-if="isLoading" class="absolute loader-circle">
				<div class="is-loading"></div>
			</div>
			<div class="property-title">
				{{ $t('Video Input Properties') }} - {{ label ? label : 'Default Video Input' }}
			</div>
			<button
				class="btn btn-default btn-sm btn-icon property-toggle"
				@click="isVisible = !isVisible">
				<i class="material-icons" v-if="isVisible">chevron_left</i>
				<i class="material-icons" v-else>chevron_right</i>
			</button>
		</div>
		<div class="property-content pr-2" :class="{'pointer-events-none': isLocked	? isLocked.is_locked   : true}">
			<div class="form-group mb-5 pr-2px">
			<div>
				<label class="text-primary">{{ $t('Select Input') }}</label>
				<custom-input
					class="mb-3"
					align="left"
					:label="$t('Label')"
					:required="isRequired"
					@blur="save"
					@keyenter="save"
					:max="40"
					:restrictChar="true"
					v-model="label"/>
			</div>
			<div class="select w-100 relative">
				<div class="mb-2 mh-40px">
					<multiselect class="select-input" v-model="valueVideo" deselect-label="Can't remove this value" track-by="deviceId" label="label"
					placeholder="Video Input" :options="filteredVideoInput" @input="save" :searchable="false" :allow-empty="false" :show-labels="false">
					<span class="arrow-multiselect" slot="caret">
						<i class="material-icons pb-1 mt-1 ml-2 size-arrow">expand_more</i>
					</span>
					<template slot="singleLabel" slot-scope="{ option }">{{ option.label }}</template>
					</multiselect>
				</div>
				<div class="mb-2 mh-40px">
					<multiselect class="select-input" v-model="valueAudio" deselect-label="Can't remove this value" track-by="deviceId" label="label"
					placeholder="Audio Input" :options="filteredAudioInput" @input="save" :searchable="false" :allow-empty="false" :show-labels="false">
					<span class="arrow-multiselect" slot="caret">
						<i class="material-icons pb-1 mt-1 ml-2 size-arrow">expand_more</i>
					</span>
					<template slot="singleLabel" slot-scope="{ option }">{{ option.label }}</template>
					</multiselect>
				</div>
			</div>
			<div class="d-flex">
				<custom-input
					class="mr-1"
					type="number"
					:label="$t('Opacity')"
					:required="isRequired"
					:min="0"
					:max="100"
					:step="1"
					:suffix="'%'"
					v-model="opacity"
					@blur="save"
					@keyenter="onInputEnter('opacity', ...arguments)"
				/>
				<!-- layer -->
				<div class="col-md-6 ml-a mb-4 pl-1 mr-0 pr-0">
					<custom-input
						type="number"
						field="index"
						:label="$t('Layer')"
						:required="isRequired"
						:max="100"
						:index-duplicated="isIndexDuplicated"
						@blur="save"
						@keyenter="onInputEnter('layer', ...arguments)"
						v-model="layerIndex"
					/>
				</div>
			</div>
			<!-- muted audio -->
				<div class="mb-2" v-if="!isMuted">
					<md-switch
						class="md-primary mt-0  mb-4"
						@change="openMutedAudio"
						v-model="isMuted">
						{{ $t('Mute Audio') }}
					</md-switch>
				</div>
				<div class="ml-0 pl-0 mb-2" v-if="isMuted">
					<md-switch
						class="md-primary mt-0  mb-4"
						@change="openUnMutedAudio"
						v-model="isMuted">
						{{ $t('Mute Audio') }}
					</md-switch>
				</div>
			<!-- Border -->
			<div>
				<div class="d-flex justify-content-between">
					<div>
						<label class="text-primary">{{ $t('Border') }}</label>
					</div>
					<div class="ml-0 pl-0">
						<md-switch
							class="md-primary mt-0 mb-2 mr-0"
							@change="save"
							v-model="isEnableBorder">
						</md-switch>
					</div>
				</div>
			</div>
			<div class="row mb-0 pb-0">
				<div class="col-md-6 pr-1 mr-0">
					<custom-input
						class="mb-4 pb-0"
						type="number"
						:label="$t('Thickness')"
						:required="isRequired"
						:min="0"
						:max="1000"
						:step="1"
						:disabled="!isEnableBorder"
						v-model="borderThickness"
						@input="onKeywordChange"
						@keyenter="onInputEnter('borderThickness', ...arguments)"
						@blur="save"
					/>
				</div>
				<div class="col-md-6 pl-1 ml-0">
					<custom-input
						type="color"
						align="left"
						picker-position="right"
						:label="$t('Color')"
						:required="isRequired"
						:disabled="!isEnableBorder"
						:class="{'disabled': !isEnableBorder}"
						:isEnableBorder="isEnableBorder"
						@input="onKeywordChange"
						@keyenter="save"
						v-model="borderColor"
						@blur="save"
					/>
				</div>
			</div>
			<div class="mt-2">
				<div class="d-flex justify-content-between">
					<div>
						<label class="text-primary">{{ $t('Crop media') }}</label>
					</div>
					<div class="ml-0 pl-0">
						<md-switch
							class="md-primary mt-0 mb-2 mr-0"
							@change="save"
							v-model="isEnableCrop">
						</md-switch>
					</div>
				</div>
			</div>
			<div class="row">
				<div class="col-md-6 pr-1 mr-0">
						<custom-input
							class="mb-3"
							type="number"
							:label="$t('Top')"
							:required="isRequired"
							:min="0"
							:max="8000"
							:step="1"
							:suffix="'px'"
							v-model="cropTop"
							:disabled="!isEnableCrop"
							@input="onKeywordChange"
							@keyenter="onInputEnter('top', ...arguments)"
							@blur="save"
						/>
					</div>
					<div class="col-md-6 pl-1 ml-0">
						<custom-input
							class="mb-3"
							type="number"
							:label="$t('Bottom')"
							:required="isRequired"
							:min="0"
							:max="8000"
							:step="1"
							:suffix="'px'"
							v-model="cropBottom"
							:disabled="!isEnableCrop"
							@input="onKeywordChange"
							@keyenter="onInputEnter('bottom', ...arguments)"
							@blur="save"
						/>
					</div>
					<div class="col-md-6 pr-1 mr-0">
						<custom-input
							class="mb-2"
							type="number"
							:label="$t('Left')"
							:required="isRequired"
							:min="0"
							:max="8000"
							:step="1"
							v-model="cropLeft"
							:suffix="'px'"
							:disabled="!isEnableCrop"
							@input="onKeywordChange"
							@keyenter="onInputEnter('left', ...arguments)"
							@blur="save"
						/>
					</div>
					<div class="col-md-6 pl-1 ml-0">
						<custom-input
							class="mb-4"
							type="number"
							:label="$t('Right')"
							:required="isRequired"
							:min="0"
							:max="8000"
							:step="1"
							v-model="cropRight"
							:suffix="'px'"
							:disabled="!isEnableCrop"
							@input="onKeywordChange"
							@keyenter="onInputEnter('right', ...arguments)"
							@blur="save"
						/>
					</div>
			</div>
			<div class="form-group mt-2">
				<position-settings
					class="mb-3"
					:width.sync="width"
					:height.sync="height"
					:position.sync="position"
					:x.sync="x"
					:y.sync="y"
					:isRatio.sync="isRatio"
					:isPosition.sync="isPosition"
					@save="save"
					@reset="resetSizePosition"
					@change="onKeywordChange"
					@keyenter="save"
					:blendmode.sync="blendMode"
					:isBlendActive="isBlendActive"
					@rescale="rescaleSize"
					:type="'video_input'"
					/>
			</div>
			<!-- on complete label -->
					<!-- <div class="mt-2">
						<div class="d-flex justify-content-between">
							<div>
								<label class="text-primary">{{ $t('On Complete') }}</label>
							</div>
						</div>
					</div> -->
						<!-- on complete controller -->
			<!-- <div class="">
				<md-menu
					:md-active.sync="isOnComplete"
					class="h-fc col-md-12 pr-0 pl-0"
					md-size="small"
					md-direction="top-start"
					md-align-trigger>
					<div v-if="selectedOnComplete"
						class="form-control cur-p relative mb-4"
						md-menu-trigger>
						<item-option
							:item="selectedOnComplete"
							v-if="selectedOnComplete"
						/>
						<div @click.stop="removeOnComplete" class="modal-close-assets absolute delete-complete-add">
							<i class="material-icons">cancel</i>
						</div>
					</div>
					<div v-else md-menu-trigger>
						<input
							type="text"
							class="form-control mb-4"
							:placeholder="$t('Stop (default)')"
							@input="onKeywordChange"
							v-model="keywordSearchOnComplete"
							@click.stop="toggleOnComplete"
						/>
						<item-option
							:item="selectedOnComplete"
							v-if="selectedOnComplete"
						/>
					</div>
					<md-menu-content id="scrolls-style"
					:class="{'macro-content-wrapper-list': filteredParent.length > 6, 'assets-wrapper-list-empty': filteredParent.length === 0,
						'assets-wrapper-list-1': filteredParent.length === 1, 'assets-wrapper-list-2': filteredParent.length === 2,
						'assets-wrapper-list-3': filteredParent.length === 3, 'assets-wrapper-list-4': filteredParent.length === 4,
						'assets-wrapper-list-5': filteredParent.length === 5, 'assets-wrapper-list-6': filteredParent.length === 6,
						}"
						>
						<md-menu-item class="item-macros-rundown macro-content-wrapper-list"
							v-for="rundownItem in filteredParent"
							:key="rundownItem.id"
							@click="selectOnComplete(rundownItem)">
							<item-option :item="rundownItem" />
						</md-menu-item>
						<md-menu-item v-if="filteredParent.length <= 0" class="item-macros-rundown macro-content-wrapper-list">
							Oops, there is no assets available
						</md-menu-item>
					</md-menu-content>
				</md-menu>
			</div> -->
			<!-- parent controller -->
			<div class="mt-2">
			<div class="d-flex justify-content-between">
				<div>
					<label class="text-primary">{{ $t('Parent Controller') }}</label>
				</div>
				<div class="ml-0 pl-0">
					<md-switch
						class="md-primary mt-0 mb-2 mr-0"
						@change="save()"
						v-model="isParent">
					</md-switch>
				</div>
			</div>
			</div>
			<div class="d-flex select ">
				<!-- <div class="select w-75 relative">
					<div class="mb-2 mh-40px mr-4">
						<multiselect class="select-input" v-model="parentItem" select-label="" deselect-label="Remove" track-by="id" label="name"
						openDirection="bottom" :optionsLimit="10" placeholder="Search or Select Parent Item" :options="filteredParent" @input="save()"
						:searchable="true" :allow-empty="true" :show-labels="true" :selectedLabel="''">
						<span class="arrow-multiselect" slot="caret">
							<i class="material-icons pb-1 mt-1 ml-2 size-arrow">expand_more</i>
						</span>
						<template slot="singleLabel" slot-scope="{ option }">{{ option.name }}</template>
						</multiselect>
					</div>
				</div> -->
				<!-- new dropdown select assets -->
				<md-menu
					:md-active.sync="isToggleParent"
					class="h-fc mr-2"
					md-size="small"
					md-direction="bottom-start"
					md-align-trigger>
					<div v-if="selectedParent"
						class="form-control cur-p relative w-230px"
						md-menu-trigger>
						<item-option
							:item="selectedParent"
							v-if="selectedParent"
						/>
						<div @click.stop="removeParent" class="modal-close-assets absolute delete-assets-add">
							<i class="material-icons">cancel</i>
						</div>
					</div>
					<div v-else md-menu-trigger>
						<input
							type="text"
							class="form-control w-230px"
							:placeholder="$t('Search or Select Parent Item')"
							v-model="keywordSearchParent"
							@click.stop="toggleParent"
						/>
						<item-option
							:item="selectedParent"
							v-if="selectedParent"
						/>
					</div>
					<md-menu-content id="scrolls-style" class="macro-content-wrapper-list">
						<md-menu-item class="item-macros-rundown macro-content-wrapper-list"
							v-for="rundownItem in filteredParent"
							:key="rundownItem.id"
							@click="selectParent(rundownItem)">
							<item-option :item="rundownItem" />
						</md-menu-item>
						<md-menu-item v-if="filteredParent.length <= 0" class="item-macros-rundown macro-content-wrapper-list">
							Oops, there is no assets available
						</md-menu-item>
					</md-menu-content>
				</md-menu>
				<!-- delay -->
				<div class="select">
					<custom-input
						class="form-input mb-3"
						type="number"
						:min="-15000"
						:max="15000"
						:step="100"
						:suffix="'ms'"
						v-model="childOrder"
						@keyenter="onInputEnter('delay', ...arguments)"
						@blur="save"
						:allowNegative="true"
					/>
					<!-- <div class="mb-2 mh-60px">
						<multiselect class="select-input" v-model="childOrder" select-label=""
							track-by="id" label="name" openDirection="bottom" placeholder="No Delay" :options="delayList" @input="save()"
							:searchable="true" :allow-empty="true" :show-labels="false">
							<span class="arrow-multiselect" slot="caret">
								<i class="material-icons pb-1 mt-1 ml-2 size-arrow">expand_more</i>
							</span>
							<template slot="singleLabel" slot-scope="{ option }">{{ option.name }}</template>
						</multiselect>
					</div> -->
				</div>
			</div>
		</div>
		</div>
		<!-- Muted Confirmation -->
			<modal
				size="small"
				@close="cancelMuted"
				v-if="isShowModalConfirnMuted">
				<template slot="header">
					<span v-if="isMuted">{{ $t('Muted Audio') }}</span>
					<span v-if="!isMuted">{{ $t('Unmuted Audio') }}</span>
				</template>
				<template slot="body">
					<span v-if="isMuted">{{ $t('Are you sure want to mute the Audio') }}?</span>
					<span v-if="!isMuted">{{ $t('Are you sure want to unmute the Audio') }}?</span>
				</template>
				<template slot="footer">
					<!-- muted -->
					<div class="w-100 flex-center">
						<button
							@click="checkMuteSave()"
							class="btn btn-primary">
							<span v-if="isMuted">{{ $t('Muted') }}</span>
							<span v-if="!isMuted">{{ $t('Unmuted') }}</span>
						</button>
						<button
							class="btn btn-default"
							@click="cancelMuted">
							{{ $t('Cancel') }}
						</button>
					</div>
				</template>
			</modal>
	</aside>
</template>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<script>
import RTC from 'detectrtc';
import { mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import ItemOption from '@/components/ItemOption.vue';
import Modal from '@/components/Modal.vue';
import { delay } from '@/lib/helper';
import rundownItemsApi from '@/api/rundownItems';
// import fileApi from '@/api/file';
import CustomInput from '@/components/CustomInput.vue';
import PositionSettings from '@/components/PositionSettings.vue';
import {
	delayList,
} from '@/lib/statics';

export default {
	name: 'VideoInputProperty',
	props: {
		data: {
			type: Object,
		},
		rundown: {
			type: Object,
		},
		media: {
			type: Object,
		},
		filteredItems: {
			type: Array,
		},
		items: {
			type: Array,
			default: () => [],
		},
	},
	components: {
		Multiselect,
		ItemOption,
		PositionSettings,
		CustomInput,
		Modal,
	},
	data() {
		return {
			videoId: '',
			audioId: '',
			label: '',
			layerIndex: 0,
			width: 0,
			height: 0,
			position: 'bottom-left',
			x: 0,
			y: 0,
			Type: 'video_input',
			progress: 0,
			isUploading: false,
			isSaving: false,
			isVisible: true,
			isLoading: false,
			valueVideo: '',
			valueAudio: '',
			audioOptions: [],
			videoOptions: [],
			isRequired: false,
			isRatio: true,
			isPosition: false,
			stream: null,
			isMuted: true,
			cropTop: 0,
			cropBottom: 0,
			cropLeft: 0,
			cropRight: 0,
			isEnableCrop: false,
			isEnableBorder: false,
			borderColor: '#FFFFFF',
			borderThickness: 1,
			isActiveEdit: false,
			isShowModalConfirnMuted: false,
			parentItem: null,
			isParent: false,
			delayList,
			childOrder: 0,
			selectedParent: null,
			selectedOnComplete: null,
			keywordSearchParent: '',
			keywordSearchOnComplete: '',
			isToggleParent: false,
			isOnComplete: false,
			blendMode: 'normal',
			isBlendActive: true,
			opacity: 100,
		};
	},
	methods: {
		toggleParent() {
			this.isToggleParent = true;
		},
		toggleOnComplete() {
			this.isOnComplete = true;
		},
		openUnMutedAudio() {
			this.isShowModalConfirnMuted = true;
			this.isMuted = false;
		},
		openMutedAudio() {
			this.isShowModalConfirnMuted = true;
			this.isMuted = true;
		},
		cancelMuted() {
			this.isShowModalConfirnMuted = false;
			this.isMuted = !this.isMuted;
		},
		updateActiveEdit() {
			this.isSaving = true;
			const callback = (response) => {
				const item = response.data;
				this.$emit('add', item);
				this.isSaving = false;
			};
			const errorCallback = () => {
				this.isSaving = false;
			};
			const params = {
				active_edit: null,
			};
			rundownItemsApi.update(this.data.id, params, callback, errorCallback);
		},
		initDevices() {
			RTC.load(() => {
				RTC.MediaDevices.map((device) => {
					if (device.kind === 'videoinput') {
						this.videoOptions.push(device);
					} else if (device.kind === 'audioinput') {
						this.audioOptions.push(device);
					}
					return device;
				});
				this.initData();
			});
		},
		stopVideoOnly(stream) {
			if (stream) {
				stream.getTracks().forEach((track) => {
					if (track) {
						track.stop();
					}
				});
			}
		},
		initData() {
			this.isVisible = true;
			this.reset();
			if (this.data) {
				let content = this.data.content;
				const activeEdit = this.data.active_edit;
				if (activeEdit && activeEdit !== this.userId) {
					this.isActiveEdit = true;
				} else {
					this.isActiveEdit = false;
				}
				if (typeof content === 'string') {
					content = JSON.parse(content);
				}
				this.label = this.data.name;
				this.layerIndex = this.data.layer_index;
				if (content) {
					const indexVideo = this.videoOptions.findIndex(({ deviceId }) => deviceId === content.videoId);
					this.valueVideo = this.videoOptions[indexVideo];
					const indexAudio = this.audioOptions.findIndex(({ deviceId }) => deviceId === content.audioId);
					this.valueAudio = this.audioOptions[indexAudio];
					this.width = content.width;
					this.height = content.height;
					this.position = content.position;
					this.x = content.x;
					this.y = content.y;
					this.isRatio = content.isRatio;
					this.isPosition = content.isPosition;
					this.isMuted = content.isMuted;
					this.cropTop = content.cropTop || 0;
					this.cropBottom = content.cropBottom || 0;
					this.cropLeft = content.cropLeft || 0;
					this.cropRight = content.cropRight || 0;
					this.isEnableCrop = content.isEnableCrop;
					this.isEnableBorder = content.isEnableBorder;
					this.borderColor = content.borderColor || '#FFFFFF';
					this.borderThickness = content.borderThickness || 0;
					this.isParent = content.isParent || false;
					// this.parentItem = this.filteredParent.find(item => item.id === content.parentItem) || '';
					this.selectedParent = this.filteredParent.find(item => item.id === content.parentItem) || '';
					this.selectedOnComplete = this.filteredParent.find(item => item.id === content.onCompleteItem) || '';

					this.childOrder = content.childOrder || 0;
					this.blendMode = content.blendMode || 'normal';
					this.opacity = content.opacity * 100;
				}
			}
		},
		reset() {
			this.videoId = '';
			this.audioId = '';
			this.label = '';
			this.layerIndex = 0;
			this.width = 1920;
			this.height = 1080;
			this.position = 'default';
			this.x = 0;
			this.y = 0;
			this.cropTop = 0;
			this.cropBottom = 0;
			this.cropLeft = 0;
			this.cropRight = 0;
			this.isEnableCrop = 0;
			this.borderThickness = 0;
			this.isEnableBorder = 0;
			this.borderColor = '#FFFFFF';
			this.blendMode = 'normal';
			this.opacity = 100;
		},
		checkMuteSave() {
			const callback = () => {
				// insert your function here
				this.isShowModalConfirnMuted = false;
			};
			const errCallback = () => {
				// insert your function here
				this.isShowModalConfirnMuted = false;
			};
			const params = {
				id: this.data.id || null,
				status: this.isMuted,
			};
			rundownItemsApi.mute(params.id, params.status, callback, errCallback);
			this.save();
		},
		save() {
			// const parentItemId = this.parentItem && this.parentItem.id ? this.parentItem.id : null;
			const parentItemId = this.selectedParent && this.selectedParent.id ? this.selectedParent.id : null;
			const onCompleteItemId = this.selectedOnComplete && this.selectedOnComplete.id ? this.selectedOnComplete.id : null;

			const content = {
				label: this.label,
				layer_index: this.layerIndex,
				width: this.width,
				height: this.height,
				position: this.position,
				x: this.x,
				y: this.y,
				type: this.type,
				isRatio: this.isRatio,
				isPosition: this.isPosition,
				isMuted: this.isMuted,
				cropTop: this.cropTop,
				cropBottom: this.cropBottom,
				cropLeft: this.cropLeft,
				cropRight: this.cropRight,
				isEnableCrop: this.isEnableCrop,
				isEnableBorder: this.isEnableBorder,
				borderColor: this.borderColor,
				borderThickness: this.borderThickness,
				isParent: this.isParent,
				parentItem: parentItemId,
				onCompleteItem: onCompleteItemId,
				childOrder: this.childOrder,
				blendMode: this.blendMode,
				opacity: this.opacity / 100,
			};
			content.audioId = this.valueAudio && this.valueAudio.deviceId ? this.valueAudio.deviceId : '';
			content.videoId = this.valueVideo && this.valueVideo.deviceId ? this.valueVideo.deviceId : '';
			const contentString = JSON.stringify(content);
			const name = this.label ? this.label : 'Default Video Input';
			const params = {
				name,
				type: this.type,
				content: contentString,
				layer_index: this.layerIndex,
				rundown_id: this.rundownId,
			};
			this.isSaving = true;
			const callback = (response) => {
				const item = response.data;
				this.$emit('add', item);
				this.isSaving = false;
			};
			const errorCallback = () => {
				this.isSaving = false;
			};
			if (!this.data.active_edit) {
				params.active_edit = this.userId;
			}
			if (this.data) {
				rundownItemsApi.update(this.data.id, params, callback, errorCallback);
			} else {
				rundownItemsApi.create(params, callback, errorCallback);
			}
		},
		checkDuplicateIndex() {
			const valueArr = this.filteredItems.map(a => a.layer_index);
			let count = 0;
			for (let i = 0; i < valueArr.length; ++i) {
				if (valueArr[i] === this.layerIndex) count++;
			}
			if (count > 1) {
				return true;
			}
			return false;
		},
		duplicateLayerText() {
			const text1 = 'Other item also using this layer, only one item at a time will be displayed';
			return text1;
		},
		onSelectVideo() {
			// this.valueVideo = device.deviceId;
		},
		onSelectAudio() {
			// this.valueAudio = device.deviceId;
		},
		onKeywordChange() {
			delay(() => {
				this.save();
			}, 1200);
		},
		resetSizePosition() {
			this.x = 0;
			this.y = 0;
			this.position = 'default';
			this.width = 1920;
			this.height = 1080;
			// this.dd = true;
			this.save();
		},
		selectParent(item) {
			this.selectedParent = item;
			this.keywordSearchParent = '';
			this.save();
		},
		removeParent() {
			this.selectedParent = null;
			this.keywordSearchParent = '';
			this.save();
		},
		selectOnComplete(item) {
			this.selectedOnComplete = item;
			this.keywordSearchOnComplete = '';
			this.save();
		},
		removeOnComplete() {
			this.selectedOnComplete = null;
			this.keywordSearchOnComplete = '';
			this.save();
		},
		onInputEnter(props, value) {
			if (props === 'layer') this.layerIndex = value;
			if (props === 'borderThickness') this.borderThickness = value;
			if (props === 'top') this.cropTop = value;
			if (props === 'bottom') this.cropBottom = value;
			if (props === 'left') this.cropLeft = value;
			if (props === 'right') this.cropRight = value;
			if (props === 'delay') this.childOrder = value;
			if (props === 'opacity') this.opacity = value;
			this.save();
		},
		rescaleSize() {
			if (this.data.content && this.rundown) {
				const displayWidth = this.rundown.display && this.rundown.display.displaySize.width ? this.rundown.display.displaySize.width : 1920;
				const displayHeight = this.rundown.display && this.rundown.display.displaySize.height ? this.rundown.display.displaySize.height : 1080;
				const spaceCanvasWidth = displayWidth - this.width;
				const spaceCanvasHeight = displayHeight - this.height;
				this.position = 'custom';
				// calculate width and height to keeping the aspect ratio based on the output size on rundown
				const ratioOriginal = Math.min(displayWidth / 1920, displayHeight / 1080);
				// check condition if image not fill canvas & not fit to canvas
				if (((spaceCanvasWidth === 0 && spaceCanvasHeight === 0) || (this.mediaX === 0 && this.mediaY === 0)) || ((this.height !== 1920 * ratioOriginal) && (this.height !== 1080 * ratioOriginal))) {
					this.width = 1920 * ratioOriginal;
					this.height = 1080 * ratioOriginal;
					// change position
					this.x = (displayWidth - this.width) / 2;
					this.y = (displayHeight - this.height) / 2;
				// scaling to fill no matter what the size of media
				} else {
					const ratioFill = Math.max(displayWidth / 1920, displayHeight / 1080);
					this.width = 1920 * ratioFill;
					this.height = 1080 * ratioFill;
					// change position
					this.x = 0;
					this.y = 0;
				}
				this.save();
			}
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
		}),
		filteredParent() {
			const filteredParent = this.items.filter(item => item.id !== this.data.id);
			let finalResult = filteredParent.filter(item => item.type !== 'separator');
			if (this.keywordSearchParent) {
				finalResult = finalResult.filter(item => item.name.toUpperCase().includes(this.keywordSearchParent.toUpperCase()));
				return finalResult;
			}
			if (this.keywordSearchOnComplete) {
				finalResult = finalResult.filter(item => item.name.toUpperCase().includes(this.keywordSearchOnComplete.toUpperCase()));
				return finalResult;
			}
			return finalResult;
		},
		userId() {
			const userId = this.user && this.user.id ? this.user.id : null;
			return userId;
		},
		userSettings() {
			const settings = this.user && this.user.settings ? JSON.parse(this.user.settings) : null;
			return settings;
		},
		rundownId() {
			return this.rundown ? this.rundown.id : 0;
		},
		isIndexDuplicated() {
			if (this.data) {
				// eslint-disable-next-line
				const filter = (({ id, layer_index }) => id !== this.data.id && layer_index === this.layerIndex);
				const duplicateItems = this.items.filter(filter);
				return duplicateItems.length > 0;
			}
			return false;
		},
		isLocked() {
			const index = this.items.findIndex(({ id }) => id === this.data.id);
			return this.items[index];
		},
		filteredVideoInput() {
			return this.videoOptions || [];
		},
		filteredAudioInput() {
			return this.audioOptions || [];
		},
		isUserEditing() {
			const index = this.items.findIndex(({ id }) => id === this.data.id);
			return this.items[index] ? this.items[index].active_edit : null;
		},
	},
	watch: {
		data() {
			if (this.data) {
				const activeEdit = this.data.active_edit;
				if (activeEdit && activeEdit !== this.userId) {
					this.isActiveEdit = true;
					// this.initData();
				} else {
					this.isActiveEdit = false;
				}
				if (this.data) {
					let content = this.data.content;
					if (typeof content === 'string') {
						content = JSON.parse(content);
					}
					this.label = this.data.name;
					this.layerIndex = this.data.layer_index;
					if (content) {
						this.width = content.width;
						this.height = content.height;
						this.position = content.position;
						this.x = content.x;
						this.y = content.y;
						this.isRatio = content.isRatio;
						this.isPosition = content.isPosition;
						this.isMuted = content.isMuted;
						this.cropTop = content.cropTop || 0;
						this.cropBottom = content.cropBottom || 0;
						this.cropLeft = content.cropLeft || 0;
						this.cropRight = content.cropRight || 0;
						this.isEnableCrop = content.isEnableCrop;
						this.isEnableBorder = content.isEnableBorder;
						this.borderColor = content.borderColor || '#FFFFFF';
						this.borderThickness = content.borderThickness || 0;
						this.isParent = content.isParent;
						// this.parentItem = this.filteredItems.find(item => item.id === content.parentItem) || '';
						this.selectedParent = this.filteredItems.find(item => item.id === content.parentItem) || '';
						this.selectedOnComplete = this.filteredParent.find(item => item.id === content.onCompleteItem) || '';
						this.childOrder = content.childOrder;
						this.blendMode = content.blendMode;
						this.opacity = content.opacity * 100;
					}
				}
			}
		},
	},
	mounted() {
		navigator.permissions.query({ name: 'camera' }).then((result) => {
			if (result.state === 'granted') {
				// permission has already been granted, no prompt is shown
				this.initDevices();
			} else if (result.state === 'prompt') {
				// there's no peristent permission registered, will be showing the prompt
				navigator.mediaDevices.getUserMedia({
					video: true,
					audio: true,
				}).then((stream) => {
					this.stream = stream;
					this.initDevices();
				}, () => {
				});
			} else if (result.state === 'denied') {
				// permission has been denied
			}
		});
	},
	beforeDestroy() {
		this.stopVideoOnly(this.stream);
		if (this.data.active_edit === this.userId) {
			this.updateActiveEdit();
		}
	},
	created() {
		this.isRequired = false;
		this.$nextTick(() => {
		});
		this.isLoading = true;
		setTimeout(() => {
			this.isRequired = true;
			this.isLoading = false;
		}, 400);
	},

};
</script>
